import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import api from '../../../services/api';
import { authService } from '../../../services/authService';
import { SidebarLayoutMode } from '../../../config/sidebarLayout';

export type BookmarksDisplayMode = 'dropdown' | 'bar' | 'pins' | 'sidebar' | 'none';

export type DetailViewLayoutPreference = 'card' | 'compact' | 'minimal';

export type EditFormLayoutPreference = 'default' | 'compact' | 'minimal';

export interface UserSettings {
  id: number;
  userId: number;
  defaultPageSize: number;
  tableDensity: 'compact' | 'normal' | 'comfortable';
  dateFormat?: string;
  dateLocale?: string;
  theme?: 'light' | 'dark' | 'ocean' | 'harbor' | 'slate' | 'auto';
  bookmarksDisplayMode?: BookmarksDisplayMode;
  detailViewLayout?: DetailViewLayoutPreference;
  editFormLayout?: EditFormLayoutPreference;
  /** User override; null = organization default (vessel accounts fall back to rail). */
  sidebarLayout?: SidebarLayoutMode | null;
  /** Persisted expand/collapse state for sidebar groups (key = menu item name). */
  sidebarExpandedGroups?: Record<string, boolean>;
}

export interface DashboardGridSettings {
  gridColumns: number;
  statCardWidth: number;
  listWidgetWidth: number;
  chartWidth: number;
  fullWidth: number;
}

export interface ApplicationSettings {
  id: number;
  enableAdvancedSearch: boolean;
  enableExport: boolean;
  enableDarkMode: boolean;
  enableTablePresets: boolean;
  enableConditionalFormatting: boolean;
  enableFooter: boolean;
  enableAiEmails: boolean;
  compactEmailView?: boolean;
  apiTimeout: number;
  appName?: string;
  gemiAddressMatchKeywords?: string[];
  defaultTablePageSize: number;
  defaultTableDensity: 'compact' | 'normal' | 'comfortable';
  pageSizeOptions?: number[];
  dashboardGridColumns?: number;
  dashboardStatCardWidth?: number;
  dashboardListWidgetWidth?: number;
  dashboardChartWidth?: number;
  dashboardFullWidth?: number;
  enableEmailNotifications?: boolean;
  'mail.enabled'?: boolean;
  'mail.host'?: string;
  'mail.port'?: number;
  'mail.secure'?: boolean;
  'mail.user'?: string;
  'mail.from'?: string;
  'mail.passwordSet'?: boolean;
  'mail.passwordSource'?: 'database' | 'environment' | 'none';
  createdAt: string;
  updatedAt: string;
  sidebarStyle?: 'classic' | 'rail' | 'tabs' | 'workspaces' | 'topmenu' | 'sidebar_topmenu';
  /** Ordered nationality ids (1–6) used as columns on Rank requirements. */
  'crew.requirementNationalityIds'?: number[];
  'openai.model'?: string;
  'openai.embeddingModel'?: string;
  'openai.visionModel'?: string;
  'openai.visionMaxPages'?: number;
  'openai.apiKeySet'?: boolean;
  'openai.apiKeySource'?: 'database' | 'environment' | 'none';
  'purchasing.publicRfqFrontendBaseUrl'?: string;
  'purchasing.publicRfqFrontendBaseUrlSource'?: 'database' | 'environment' | 'none';
  'general.vesselAppUrl'?: string;
  'general.vesselAppUrlSource'?: 'database' | 'environment' | 'none';
}

// User Settings Hooks (only fetch when authenticated to avoid 401)
export const useUserSettings = () => {
  const token = authService.getToken();
  const { data, error, isLoading, mutate } = useSWR<UserSettings>(
    token ? '/settings/user' : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data,
    userSettings: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Application Settings Hooks (only fetch when authenticated to avoid 401)
export const useApplicationSettings = () => {
  const token = authService.getToken();
  const { data, error, isLoading, mutate } = useSWR<ApplicationSettings>(
    token ? '/settings/application' : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data,
    applicationSettings: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export type CustomSettingType = 'string' | 'number' | 'boolean' | 'secret' | 'json';

export interface CustomSetting {
  key: string;
  value: unknown;
  valueType: CustomSettingType;
  description: string;
  isActive: boolean;
  secretSet?: boolean;
  updatedAt: string;
}

export const useCustomSettings = () => {
  const token = authService.getToken();
  const { data, error, isLoading, mutate } = useSWR<CustomSetting[]>(
    token ? '/settings/custom' : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return {
    customSettings: data ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

/** Whether transactional email (e.g. task notifications) is enabled. Uses same backend check as sending. */
export const useEmailNotificationsEnabled = () => {
  const token = authService.getToken();
  const { data, mutate } = useSWR<{ enabled: boolean }>(
    token ? '/settings/application/email-notifications-enabled' : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  return { enabled: data?.enabled === true, mutate };
};

/** Whether task notification is enabled: email notifications on AND "Task Notification" template enabled. */
export const useTaskNotificationEnabled = () => {
  const token = authService.getToken();
  const { data, mutate } = useSWR<{ enabled: boolean }>(
    token ? '/settings/application/task-notification-enabled' : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  return { enabled: data?.enabled === true, mutate };
};

// System email templates (admin)
export interface SystemEmailTemplateListItem {
  key: string;
  description: string;
  placeholders: string[];
  subject: string;
  bodyHtml: string | null;
  isEnabled: boolean;
  updatedAt: string | null;
}

export interface SystemEmailTemplateGlobalPlaceholder {
  placeholder: string;
  label: string;
}

export interface SystemEmailTemplatesResponse {
  templates: SystemEmailTemplateListItem[];
  globalPlaceholders: SystemEmailTemplateGlobalPlaceholder[];
}

export interface SystemEmailTemplateDetail {
  subject: string;
  bodyHtml: string | null;
  description: string | null;
  isEnabled: boolean;
  placeholders: string[];
  globalPlaceholders?: SystemEmailTemplateGlobalPlaceholder[];
}

export const useSystemEmailTemplates = () => {
  const token = authService.getToken();
  const { data, error, isLoading, mutate } = useSWR<SystemEmailTemplatesResponse>(
    token ? '/settings/system-email-templates' : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  return {
    templates: data?.templates ?? [],
    globalPlaceholders: data?.globalPlaceholders ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSystemEmailTemplate = (key: string | null) => {
  const { data, error, isLoading, mutate } = useSWR<SystemEmailTemplateDetail | null>(
    key ? `/settings/system-email-templates/${encodeURIComponent(key)}` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  return {
    template: data ?? null,
    isLoading,
    isError: error,
    mutate,
  };
};

export async function updateSystemEmailTemplate(
  key: string,
  body: { subject: string; bodyHtml?: string; description?: string; isEnabled?: boolean }
): Promise<SystemEmailTemplateListItem> {
  const res = await api.put(`/settings/system-email-templates/${encodeURIComponent(key)}`, body);
  return res.data;
}

// Company profile (letterhead / company details)
export interface CompanyProfile {
  id: number;
  companyName: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  signatoryName: string | null;
  logoPath: string | null;
  createdAt: string;
  updatedAt: string;
}

export const useCompanyProfile = () => {
  const token = authService.getToken();
  const { data, error, isLoading, mutate } = useSWR<CompanyProfile | null>(
    token ? '/settings/company-profile' : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  return { companyProfile: data ?? null, isLoading, isError: error, mutate };
};

export async function updateCompanyProfile(body: Partial<Pick<CompanyProfile, 'companyName' | 'companyAddress' | 'companyPhone' | 'companyEmail' | 'signatoryName'>>): Promise<CompanyProfile> {
  const res = await api.patch('/settings/company-profile', body);
  return res.data;
}

/** Returns the URL to load the company logo image (API path). Used with authenticated fetch. */
export function getCompanyLogoUrl(): string {
  const base = api.defaults.baseURL ?? '';
  return `${base}/settings/company-profile/logo`;
}

/** Fetches the company logo with auth and returns a blob URL for use in img src. Revokes the URL on cleanup. */
export function useCompanyLogoBlobUrl(logoPath: string | null | undefined) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!logoPath) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(getCompanyLogoUrl(), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        if (res.status === 204 || (res.data instanceof Blob && res.data.size === 0)) {
          setBlobUrl(null);
          return;
        }
        const url = URL.createObjectURL(res.data as Blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [logoPath]);

  return { logoBlobUrl: blobUrl, loading };
}

export async function uploadCompanyLogo(file: File): Promise<{ logoPath: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/settings/company-profile/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

// Deleted file attachments (FileAttachment where isDeleted = true)
export interface DeletedFileAttachment {
  id: number;
  fileName: string | null;
  filePath: string | null;
  fileType: string | null;
  fileSize: number | null;
  uploadedAt: string;
  description: string | null;
  clientId: number | null;
  taskId: number | null;
  appointmentId: number | null;
  expenseId: number | null;
  invoiceId: number | null;
  leadId: number | null;
  folderId: number | null;
  knowledgeBaseArticleId: number | null;
  todoId: number | null;
  supportTicketId: number | null;
  client?: { clientId: number; clientName: string | null } | null;
  task?: { taskId: number; taskDescription: string | null; taskDate: string | null } | null;
  appointment?: {
    clientAppId: number;
    clientAppName: string | null;
    clientAppScheduleDateTime: string | null;
    client?: { clientId: number; clientName: string | null } | null;
  } | null;
  expense?: { id: number; description: string | null; amount: number | null; transactionDate: string | null } | null;
  invoice?: {
    invoiceId: number;
    InvoiceNo: string | null;
    invoiceDescription: string | null;
    InvoiceDate: string | null;
    client?: { clientId: number; clientName: string | null } | null;
  } | null;
  lead?: { leadId: number; leadDescr: string | null } | null;
  folder?: {
    id: number;
    clientId: number;
    client?: { clientId: number; clientName: string | null } | null;
    template?: { name: string } | null;
  } | null;
  knowledgeBaseArticle?: { id: number; title: string } | null;
  todo?: { todoId: number; todoDescr: string | null; todoDueDate: string | null } | null;
  supportTicket?: { id: number; reference: string; title: string } | null;
}

export const useDeletedFileAttachments = () => {
  const { data, error, isLoading, mutate } = useSWR<DeletedFileAttachment[]>(
    '/file-attachment/deleted',
    (url) => api.get(url).then((res) => res.data),
    { revalidateOnFocus: true, revalidateOnReconnect: true }
  );
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

